import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import classnames from "classnames";
import { memo } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { RedoBadge } from "../badge/redo-badge";
import { RedoButton } from "../buttons/redo-button";
import * as styles from "./redo-feature-notification-card.module.css";

export interface RedoFeatureNotificationCardProps {
  "data-dd-action-name"?: string;
  title: string;
  description: string;
  image?: string;
  actionText?: string;
  showBetaBadge?: boolean;
  onDismiss?: () => void;
  onAction?: () => void;
}

export const RedoFeatureNotificationCard = memo(
  function RedoFeatureNotificationCard({
    title,
    description,
    image,
    actionText: actionTextProp,
    showBetaBadge = false,
    onDismiss,
    onAction,
    "data-dd-action-name": actionName,
  }: RedoFeatureNotificationCardProps) {
    const { t } = useTranslation();
    const actionText =
      actionTextProp ?? t("Learn more", { context: "feature notification" });
    const handleAction = () => {
      onAction?.();
    };

    return (
      <Flex
        bgColor="tertiary"
        borderColor="primary"
        borderRadius="xl"
        borderStyle="solid"
        borderWidth="1px"
        className={classnames(styles.container, darkModeCss.darkMode)}
        data-arbiter="RedoFeatureNotificationCard"
        data-dd-action-name={actionName}
        flexDirection="column"
        gap="xl"
        px="xl"
        py="2xl"
        shadow="lg"
      >
        <Flex flexDirection="column" gap="xs">
          <Flex flexDirection="row" gap="sm">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {title}
            </Text>
            {showBetaBadge && (
              <RedoBadge
                color="gray"
                size="xs"
                text={t("BETA", { context: "feature badge" })}
              />
            )}
          </Flex>
          <Text fontSize="sm" textColor="secondary">
            {description}
          </Text>
        </Flex>

        {image && (
          <Flex borderRadius="lg">
            <img alt={title} className={styles.image} src={image} />
          </Flex>
        )}

        <Flex flexDirection="row" gap="lg" justify="space-between">
          <RedoButton
            hierarchy="link"
            onClick={handleAction}
            text={actionText}
            theme="action"
          />
        </Flex>
        {onDismiss && (
          <Flex className={styles.closeX}>
            <RedoButton
              aria-label={t("Dismiss", { context: "feature notification" })}
              IconLeading={XIcon}
              onClick={onDismiss}
              size="sm"
            />
          </Flex>
        )}
      </Flex>
    );
  },
);
